import { memo, Component } from 'react';
import PropTypes from 'prop-types';
import TabBar from '@alltrails/shared/denali/components/TabBar';
import LoadingSpinner from '../../shared/LoadingSpinner/LoadingSpinner';
import WeatherCard from '../WeatherCard';
import * as styles from './styles/styles.module.scss';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { getWeatherTabProp, getUvIndexProp, getDaylightProps } from '../../../utils/weather_condition_helper';

class WeatherConditions extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeTabKey: props.tabs.length ? props.tabs[0].key : '',
      tabs: props.tabs,
      loading: true
    };
  }

  static FIVE_DAY_FORCAST_URL = '/api/alltrails/weather/5-day-forecast';

  static WEATHER_KEY = 'weather';

  static UV_INDEX_KEY = 'uv_index';

  static DAYLIGHT_KEY = 'day_light';

  componentDidMount() {
    if (this.props.weatherForecast) {
      this.parseForecastData(this.props.weatherForecast.forecasts);
      return;
    }

    this.fetchForecastData();
  }

  getNewTabState(weatherDays, uvIndexDays, dayLightDays) {
    return this.state.tabs
      .map(tab => {
        let daysInfo = [];
        switch (tab.key) {
          case WeatherConditions.WEATHER_KEY:
            daysInfo = weatherDays;
            break;
          case WeatherConditions.UV_INDEX_KEY:
            daysInfo = uvIndexDays;
            break;
          case WeatherConditions.DAYLIGHT_KEY:
            daysInfo = dayLightDays;
            break;
          default:
            return tab;
        }
        return daysInfo.length ? { ...tab, days: daysInfo } : null;
      })
      .filter(tab => !!tab);
  }

  activeTabIsDaylight() {
    return this.state.activeTabKey === WeatherConditions.DAYLIGHT_KEY;
  }

  fetchForecastData() {
    const headers = this.props.locale ? { 'X-Language-Locale': this.props.locale } : {};
    const { latitude, longitude, metric } = this.props;
    const params = { latitude, longitude, metric };
    ServerCommunicationUtil.postApiEndpoint(
      WeatherConditions.FIVE_DAY_FORCAST_URL,
      params,
      this.handleApiSuccess,
      this.handleApiError,
      () => {},
      headers
    );
  }

  parseForecastData(weatherForecast) {
    const forecasts = weatherForecast.slice(0, 5);
    const { sunrise, sunset } = forecasts[0];
    const dayLightDays = sunrise && sunset ? getDaylightProps(this.props.locale, forecasts[0], this.props.intl) : [];
    const weatherDays = [];
    const uvIndexDays = [];
    forecasts.forEach(forecast => {
      const weatherTabProp = getWeatherTabProp(forecast, this.props.metric);
      if (weatherTabProp) weatherDays.push(weatherTabProp);

      const uvIndexProp = getUvIndexProp(forecast);
      if (uvIndexProp) uvIndexDays.push(uvIndexProp);
    });

    const tabs = this.getNewTabState(weatherDays, uvIndexDays, dayLightDays);
    this.setState({ tabs, loading: false });
  }

  handleApiSuccess = response => {
    this.parseForecastData(response.forecasts);
  };

  handleApiError = err => {
    console.log(err.responseJSON.errors);
  };

  handleTabChange = tabKey => {
    if (tabKey === this.state.activeTabKey) {
      return;
    }

    this.setState({ activeTabKey: tabKey });
  };

  renderActiveContent() {
    if (this.state.loading) {
      return (
        <div className={styles.loadingSpinnerContainer}>
          <LoadingSpinner />
        </div>
      );
    }

    const { days } = this.state.tabs.find(tab => tab.key === this.state.activeTabKey);

    return days.map((day, idx) => {
      return <WeatherCard key={idx} {...day} />;
    });
  }

  renderSlidingTabs() {
    return (
      <TabBar
        activeTab={this.state.activeTabKey}
        className={styles.tabBar}
        onChange={this.handleTabChange}
        testId="weather-conditions-tab-bar"
        tabs={this.state.tabs.map(tab => {
          return {
            id: tab.key,
            label: tab.label,
            testId: `weather-conditions-tab-${tab.key}`
          };
        })}
      />
    );
  }

  render() {
    return this.state.tabs.length ? (
      <div>
        {this.renderSlidingTabs()}
        <ul className={`${styles.list} ${this.activeTabIsDaylight() && styles.activeDayLight}`}>{this.renderActiveContent()}</ul>
      </div>
    ) : null;
  }
}

WeatherConditions.propTypes = {
  trailId: PropTypes.number.isRequired,
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired,
  locale: PropTypes.string.isRequired,
  metric: PropTypes.bool.isRequired,
  weatherForecast: PropTypes.shape({
    forecast: PropTypes.arrayOf(PropTypes.string),
    metadata: PropTypes.shape({
      expire_time_gmt: PropTypes.number.isRequired,
      language: PropTypes.string.isRequired,
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
      status_code: PropTypes.number.isRequired,
      transaction_id: PropTypes.string.isRequired,
      units: PropTypes.string.isRequired,
      version: PropTypes.string.isRequired
    })
  }).isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      days: PropTypes.arrayOf(
        PropTypes.shape({
          topText: PropTypes.string,
          bottomText: PropTypes.string,
          imgSrc: PropTypes.string,
          srcSet: PropTypes.arrayOf(PropTypes.string),
          overlay: PropTypes.element
        })
      )
    })
  ).isRequired
};

export default memo(WeatherConditions);
